// ════════════════════════════════════════════════════════════════════════════
//  ManDana — the bridge a manifestation speaks across
//  ---------------------------------------------------------------------------
//  One door, and everything that arrives at it was written by somebody else.
//
//  ── THERE IS NO ORIGIN TO CHECK ────────────────────────────────────────────
//  The usual rule for postMessage is "verify event.origin". It does not apply
//  here and following it would be worse than useless. A manifestation runs in a
//  sandboxed frame with no allow-same-origin, so its origin is OPAQUE, and an
//  opaque origin arrives as the literal string "null" — which any page anywhere
//  can also present. Measured, not assumed.
//
//  So authentication is two things, neither of them an origin: the component
//  that owns the frame checks `event.source === iframe.contentWindow`, and every
//  message carries the session id minted for that frame. Replies must go out
//  with targetOrigin '*' for the same reason — an opaque origin cannot be named
//  — and the session id is what stops a frame that has navigated elsewhere from
//  receiving an answer meant for the document that was there before.
//
//  ── SILENCE IS A RESPONSE, AND IT IS THE RIGHT ONE ─────────────────────────
//  A message that cannot be placed is DROPPED, never refused. "No" still tells
//  the sender that something is listening and that it got the shape roughly
//  right, and this is the one place in the app where that is worth denying.
//  A message that IS placeable but wrong gets a named refusal, because by then
//  we know who is asking and the honest thing is to say what was wrong.
//
//  ── WHY THIS IS A MODULE AND NOT A LISTENER ────────────────────────────────
//  Same argument md-link.ts makes about parsing a scheme: the decisions here are
//  security decisions, and a security decision that can only be exercised by
//  mounting a React tree and posting real messages is one nobody will exercise.
//  Everything below is pure. The listener that uses it is four lines and holds
//  no rules of its own.
// ════════════════════════════════════════════════════════════════════════════

(function (g: any) {
  'use strict';

  const MD_BRIDGE_V = 1;

  /* The complete list of what may be asked, and what each costs. There is no
     default: an op absent from this table is `unknown_op`, so adding a handler
     without adding it here REFUSES rather than allows. Deny by construction is
     worth more than deny by discipline.

     `own` is a manifestation's own private store — it needs no grant, because
     the data is the manifestation's and nobody else can see it.
     `read` and `write` are grants a person gives in this app's own chrome. */
  const MD_BRIDGE_OPS: Record<string, string> = {
    'app.context': 'own',
    'app.today': 'read',
    'records.day': 'read',
    'records.project': 'read',
    'records.get': 'read',
    'projects.list': 'read',
    'pursuits.list': 'read',
    'prayers.day': 'read',
    'records.create': 'write',
    'records.update': 'write',
    'records.complete': 'write',
    'records.open': 'read',
    'day.open': 'read',
    'projects.open': 'read',
    'app.compose': 'write',
    'data.get': 'own',
    'data.put': 'own',
    /* Phase 1D: source-bound ops. Narrower than records.* — each resolves a
       named declaration on the entry, never an account-wide handle. */
    'source.read': 'read',
    'action.invoke': 'write',
  };

  const MD_BRIDGE_CAPABILITIES: Record<string, string> = {
    'app.today': 'records.read',
    'records.day': 'records.read',
    'records.project': 'records.read',
    'records.get': 'records.read',
    'records.create': 'records.write',
    'records.update': 'records.write',
    'records.complete': 'records.write',
    'projects.list': 'projects.read',
    'pursuits.list': 'projects.read',
    'prayers.day': 'prayers.read',
    'records.open': 'navigation',
    'day.open': 'navigation',
    'projects.open': 'navigation',
    'app.compose': 'navigation',
    'source.read': 'sources.read',
    'action.invoke': 'actions.invoke',
  };

  function mdBridgeCapability(op: unknown): string | null {
    return typeof op === 'string' && Object.prototype.hasOwnProperty.call(MD_BRIDGE_CAPABILITIES, op)
      ? (MD_BRIDGE_CAPABILITIES[op] || null) : null;
  }

  /* Every refusal this app can make, with the sentence a person should see. A
     closed set: a code invented at a call site is one no interface has words
     for, so an unknown code becomes `internal` rather than travelling outward. */
  const MD_BRIDGE_MESSAGES: Record<string, string> = {
    bad_message: 'That request was not in a shape this app understands.',
    unsupported_version: 'This Craft speaks a version of the bridge this app does not.',
    unknown_op: 'This app has no such capability.',
    bad_arg: 'That request was missing something it needed, or asked for something impossible.',
    denied: 'This Craft has not been allowed to do that.',
    not_found: 'There is nothing here by that name.',
    too_large: 'That was too large to pass across the bridge.',
    rate_limited: 'This Craft is asking too quickly.',
    internal: 'Something went wrong on this side of the bridge.',
  };
  const MD_BRIDGE_CODES = Object.keys(MD_BRIDGE_MESSAGES);

  /* Bounded in both directions. postMessage structured-clones, so an unbounded
     frame can exhaust the tab's memory and no policy anywhere prevents it.

     Checking a size means serialising, which is linear in the payload — the same
     order of work as having received it, and no worse. There is no cheap size
     API for a structured clone, and pretending otherwise would be the kind of
     comment that reads as a guarantee. What actually bounds the pathological
     case is the limiter below. */
  const MD_BRIDGE_ARG_MAX = 32 * 1024;
  const MD_BRIDGE_RESULT_MAX = 256 * 1024;

  /* A burst, then a trickle. Generous enough that a dashboard drawing itself on
     load never notices, mean enough that a loop cannot spin the app. */
  const MD_BRIDGE_BURST = 64;
  const MD_BRIDGE_RATE = 8;                              // per second, sustained

  const ID_MAX = 64;

  function sizeOf(value: unknown): number {
    try {
      const s = JSON.stringify(value);
      return typeof s === 'string' ? s.length : 0;
    } catch (e) {
      return Infinity;                                   // cyclic, or not cloneable
    }
  }

  /** Read one arriving message. Never throws; the caller is an event listener. */
  function mdBridgeParse(data: unknown, sid: unknown): unknown {
    const drop = { drop: true };
    if (!data || typeof data !== 'object' || Array.isArray(data)) return drop;
    const d = data as Record<string, unknown>;

    /* Placeable, or not. Everything in this block fails SILENTLY — the message
       is not from a frame we are talking to, or not for us at all. */
    if (d.md !== 'manifest') return drop;
    if (typeof sid !== 'string' || !sid || d.sid !== sid) return drop;
    if (typeof d.id !== 'string' || !d.id || d.id.length > ID_MAX) return drop;

    /* From here on it is one of ours and gets an answer. */
    const id = d.id;
    if (d.v !== MD_BRIDGE_V) return { id, error: 'unsupported_version' };

    /* Own-property only. `constructor` and `__proto__` are on every object and
       would otherwise look like ops. */
    const op = typeof d.op === 'string' ? d.op : '';
    if (!Object.prototype.hasOwnProperty.call(MD_BRIDGE_OPS, op)) return { id, error: 'unknown_op' };

    const arg = d.arg === undefined || d.arg === null ? {} : d.arg;
    if (typeof arg !== 'object' || Array.isArray(arg)) return { id, error: 'bad_arg' };
    if (sizeOf(arg) > MD_BRIDGE_ARG_MAX) return { id, error: 'too_large' };

    return { id, op, arg };
  }

  function mdBridgeErr(sid: string, id: string, code: unknown): unknown {
    const known = typeof code === 'string' && MD_BRIDGE_MESSAGES[code] ? code : 'internal';
    return {
      md: 'manifest', v: MD_BRIDGE_V, sid, id, ok: false,
      error: { code: known, message: MD_BRIDGE_MESSAGES[known] },
    };
  }

  /** An answer, or a refusal when the answer is too big to be one. */
  function mdBridgeOk(sid: string, id: string, result: unknown): any {
    if (sizeOf(result) > MD_BRIDGE_RESULT_MAX) return mdBridgeErr(sid, id, 'too_large');
    return { md: 'manifest', v: MD_BRIDGE_V, sid, id, ok: true, result };
  }

  /** A token bucket, one per frame. `now` is injected so this is assertable
      without waiting for real seconds to pass. */
  function mdBridgeLimiter(): (now: number) => boolean {
    let tokens = MD_BRIDGE_BURST;
    let last = -1;
    return function allow(now: number): boolean {
      if (typeof now !== 'number' || !Number.isFinite(now)) return false;
      if (last < 0) last = now;
      /* Clamped at zero. A clock that steps backwards — a device waking, a
         manual change — must never read as elapsed time and hand out free
         allowance to whatever is asking. */
      const elapsed = Math.max(0, now - last);
      last = now;
      tokens = Math.min(MD_BRIDGE_BURST, tokens + (elapsed / 1000) * MD_BRIDGE_RATE);
      if (tokens < 1) return false;
      tokens -= 1;
      return true;
    };
  }

  /** What a record looks like from outside: built field by field, never spread.

      The spread is the tempting version and it is the one that leaks. The day
      somebody adds a field to a resolved record — a private note, a device id, a
      draft — a spread would publish it to every manifestation that had ever been
      allowed to read anything, and no test would fail. This function is the list
      of what a manifestation may know, and it has to be edited to grow. */
  function mdBridgeRecordOut(resolved: unknown): unknown {
    if (!resolved || typeof resolved !== 'object') return null;
    const r = resolved as Record<string, any>;
    return {
      ref: g.mdRecordRefKey ? g.mdRecordRefKey(r.ref) : '',
      title: typeof r.title === 'string' ? r.title : '',
      text: typeof r.text === 'string' ? r.text : '',
      domain: r.domainId || null,
      day: r.day || null,
    };
  }

  g.MD_BRIDGE_V = MD_BRIDGE_V;
  g.MD_BRIDGE_OPS = MD_BRIDGE_OPS;
  g.MD_BRIDGE_CAPABILITIES = MD_BRIDGE_CAPABILITIES;
  g.mdBridgeCapability = mdBridgeCapability;
  g.MD_BRIDGE_CODES = MD_BRIDGE_CODES;
  g.MD_BRIDGE_ARG_MAX = MD_BRIDGE_ARG_MAX;
  g.MD_BRIDGE_RESULT_MAX = MD_BRIDGE_RESULT_MAX;
  g.MD_BRIDGE_BURST = MD_BRIDGE_BURST;
  g.MD_BRIDGE_RATE = MD_BRIDGE_RATE;
  g.mdBridgeParse = mdBridgeParse;
  g.mdBridgeOk = mdBridgeOk;
  g.mdBridgeErr = mdBridgeErr;
  g.mdBridgeLimiter = mdBridgeLimiter;
  g.mdBridgeRecordOut = mdBridgeRecordOut;

  // CommonJS escape hatch, following md-manifest.ts and md-record.ts.
  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) {
    mod.exports = { MD_BRIDGE_V, MD_BRIDGE_OPS, MD_BRIDGE_CAPABILITIES, mdBridgeCapability, MD_BRIDGE_CODES, MD_BRIDGE_ARG_MAX,
      MD_BRIDGE_RESULT_MAX, MD_BRIDGE_BURST, MD_BRIDGE_RATE, mdBridgeParse,
      mdBridgeOk, mdBridgeErr, mdBridgeLimiter, mdBridgeRecordOut };
  }
})(typeof window !== 'undefined' ? window : globalThis);
